import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Activity, MessageSquare, ChevronRight, Plus, TrendingUp } from "lucide-react";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/admin/clients"),
          api.get("/admin/feedback/recent"),
        ]);
        setClients(a.data);
        setFeedback(b.data);
      } catch (e) { /* silenced */ }
      finally { setLoading(false); }
    })();
  }, []);

  const active = clients.filter((c) => c.subscription_status === "active").length;
  const trials = clients.filter((c) => c.subscription_status === "trial").length;
  const monthlyMRR = clients
    .filter((c) => c.subscription_status === "active")
    .reduce((s, c) => s + (c.monthly_price_eur || 0), 0);

  return (
    <AppShell>
      <div className="qv-page-head" data-testid="admin-dashboard">
        <div className="label">Panel del entrenador</div>
        <h1>Hola, {user?.name?.split(" ")[0]}.</h1>
        <p className="sub">Aquí controlas tu plantilla, sus planes y los feedbacks de cada sesión.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 32 }}>
        <div className="qv-stat" data-testid="stat-clients">
          <div className="lbl">Clientes</div>
          <div className="val">{clients.length}</div>
        </div>
        <div className="qv-stat">
          <div className="lbl">Activos</div>
          <div className="val" style={{ color: "#25D366" }}>{active}</div>
        </div>
        <div className="qv-stat">
          <div className="lbl">En prueba</div>
          <div className="val" style={{ color: "var(--qv-red)" }}>{trials}</div>
        </div>
        <div className="qv-stat">
          <div className="lbl">MRR estimado</div>
          <div className="val">{monthlyMRR.toFixed(0)}<span className="unit">€/mes</span></div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
        <div className="qv-card-app">
          <div className="qv-card-head">
            <h3>Clientes recientes</h3>
            <Link to="/app/admin/clients" className="qv-btn-ghost" style={{ fontSize: 10 }} data-testid="goto-clients">
              VER TODOS <ChevronRight size={14} />
            </Link>
          </div>
          {loading ? (
            <div style={{ color: "#737373", padding: 20 }}>Cargando...</div>
          ) : clients.length === 0 ? (
            <div className="qv-empty">
              <Users size={28} style={{ margin: "0 auto 12px", color: "#737373" }} />
              <div>Aún no tienes clientes. Crea el primero.</div>
              <Link to="/app/admin/clients" className="qv-btn-primary" style={{ marginTop: 18 }}>
                <Plus size={16} /> AÑADIR CLIENTE
              </Link>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {clients.slice(0, 5).map((c) => (
                <Link
                  key={c.id}
                  to={`/app/admin/clients/${c.id}`}
                  className="qv-row"
                  style={{ gridTemplateColumns: "1.4fr 0.8fr 0.5fr auto" }}
                  data-testid={`client-row-${c.id}`}
                >
                  <div>
                    <div style={{ fontWeight: 700 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#737373" }}>{c.email}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#a3a3a3" }}>{c.team || "—"}</div>
                  <span className={`qv-pill ${c.subscription_status}`}>{c.subscription_status}</span>
                  <ChevronRight size={16} color="#737373" />
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="qv-card-app">
          <div className="qv-card-head">
            <h3>Últimos feedback</h3>
            <MessageSquare size={16} color="#737373" />
          </div>
          {feedback.length === 0 ? (
            <div className="qv-empty" style={{ padding: 28 }}>
              <div>Sin feedbacks aún.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {feedback.slice(0, 6).map((f) => (
                <div key={f.id} style={{ padding: 12, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{f.client_name}</div>
                    <div style={{ fontSize: 10, color: "#737373", letterSpacing: "0.18em", textTransform: "uppercase" }}>
                      {new Date(f.date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 11, color: "#a3a3a3", flexWrap: "wrap" }}>
                    <span>RPE <b style={{ color: "#fff" }}>{f.rpe}</b></span>
                    <span>Energía <b style={{ color: "#fff" }}>{f.energy}/5</b></span>
                    <span>Sueño <b style={{ color: "#fff" }}>{f.sleep_hours}h</b></span>
                    <span>Estrés <b style={{ color: "#fff" }}>{f.stress}/5</b></span>
                    {f.pain && <span style={{ color: "var(--qv-red)" }}>Dolor: {f.pain_zone || "sí"}</span>}
                  </div>
                  {f.notes && <div style={{ marginTop: 8, fontSize: 12, color: "#cfcfcf" }}>"{f.notes}"</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
